import EmptyRefundableList from 'components/States/EmptyRefundableFareList';
import React, { FC, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FuelSkeleton,
  FuelTable,
  FuelThead,
  FuelTheadItem,
  FuelTrow,
  FuelTrowItem,
  FuelTypography,
} from '@frete.com/fuel-react';
import AppContext from 'context';
import { TARRIF_TAB } from 'context/reducers/tabs.type';
import { useTracking } from 'hooks/useTracking';
import { useAmplitude } from 'hooks/useAmplitude';
import { skeletonRefundableListIndexMock } from 'mocks/refundable/mocks';
import Avatar from './Avatar';
import * as Styled from './styled';

const TruckerRefundableList: FC = () => {
  const navigate = useNavigate();
  const { freightId } = useParams();
  const { sendTracking } = useTracking();
  const { sendAmplitude } = useAmplitude();

  const {
    state: {
      refundableFare: { dataRefundableFare, isFetchedRefundableFare, isFetchingRefundableFare },
      tabs: { activeTab },
    },
    dispatch,
  } = useContext(AppContext);

  const sendEventTracking = (id: string) => {
    sendTracking({
      url: window.location.href,
      referrer: document.referrer,
      event: {
        name: `trucker_listing_profile_visited`,
      },
      data: {
        source: 'aba-pagaram-tarifa',
        type: 'listagem-motorista',
        truckerId: `hashed|${id}`,
        freightId: `hashed|${freightId}`,
      },
    });

    sendAmplitude('trucker_listing_profile_visited', {
      source: 'aba-pagaram-tarifa',
      type: 'listagem-motorista',
      truckerId: id,
      freightCode: freightId,
    });
  };

  const selectedTrucker = (truckerId: string) => {
    dispatch({
      type: 'truckers_driver_active_set',
      payload: { activeDriver: { id: truckerId } },
    });

    navigate(truckerId);
  };

  const showFirstItemInTruckerProfile = () => {
    const truckerId = dataRefundableFare[0]?.person.id;

    dispatch({
      type: 'truckers_driver_active_set',
      payload: { activeDriver: { id: truckerId } },
    });

    sendEventTracking(truckerId);

    navigate(truckerId);
  };

  useEffect(() => {
    if (dataRefundableFare?.length && activeTab === TARRIF_TAB) {
      showFirstItemInTruckerProfile();
    }
  }, [dataRefundableFare, activeTab]);

  return (
    <>
      {!!dataRefundableFare?.length && (
        <Styled.WrapperTruckerRefundable>
          <FuelTable breakpoint={0}>
            <FuelThead slot="thead">
              <FuelTheadItem className="trow-item-avatar" />
              <FuelTheadItem>Nome</FuelTheadItem>
              <FuelTheadItem>Data do pagameto</FuelTheadItem>
            </FuelThead>

            <Styled.ContentTrowScroll slot="trow">
              {isFetchedRefundableFare &&
                dataRefundableFare?.map(
                  ({
                    person: { name, areaCodes, selfieUri, uuid, id },
                    vehicle: { type, body },
                    refundableFare: { paidAt },
                  }) => (
                    <FuelTrow
                      slot="trow"
                      key={uuid}
                      onClick={() => {
                        selectedTrucker(id);
                        sendEventTracking(id);
                      }}
                    >
                      <FuelTrowItem slot="item" className="trow-item-avatar">
                        <Avatar name={name} selfieUri={selfieUri} />
                      </FuelTrowItem>

                      <FuelTrowItem slot="item">
                        <FuelTypography size="body-sm" weight="bold">
                          {name}
                        </FuelTypography>

                        <FuelTypography size="caption" weight="medium" color="neutral-default">
                          {`${type.name} | ${body.name}`}
                        </FuelTypography>

                        <FuelTypography size="caption" weight="medium" color="neutral-default">
                          DDD: {areaCodes?.length > 0 ? areaCodes.map((code) => `(${code})`).join(', ') : ''}
                        </FuelTypography>
                      </FuelTrowItem>

                      <FuelTrowItem slot="item" className="trow-item-date-paidAt">
                        <FuelTypography size="body-sm" weight="medium" color="neutral-default">
                          {paidAt}
                        </FuelTypography>
                      </FuelTrowItem>
                    </FuelTrow>
                  )
                )}
            </Styled.ContentTrowScroll>
          </FuelTable>
        </Styled.WrapperTruckerRefundable>
      )}

      {isFetchingRefundableFare && (
        <Styled.WrapperTruckerRefundable>
          <FuelTable breakpoint={0}>
            <FuelThead slot="thead">
              <FuelTheadItem className="trow-item-avatar" />
              <FuelTheadItem>Nome</FuelTheadItem>
              <FuelTheadItem>Data do pagameto</FuelTheadItem>
            </FuelThead>

            <Styled.ContentTrowScroll slot="trow" data-testid="skeleton-trucker-list-refundable">
              {skeletonRefundableListIndexMock?.map((skeleton: string) => (
                <FuelTrow slot="trow" key={skeleton}>
                  <FuelTrowItem slot="item" className="trow-item-avatar">
                    <FuelSkeleton width="40px" height="40px" shape="circle" size="huge" />
                  </FuelTrowItem>

                  <FuelTrowItem slot="item">
                    <FuelTypography size="body-sm" weight="bold">
                      <FuelSkeleton width="100px" height="40px" shape="text" size="small" />
                    </FuelTypography>

                    <FuelTypography size="caption" weight="medium" color="neutral-default">
                      <FuelSkeleton width="80px" height="40px" shape="text" size="xsmall" />
                    </FuelTypography>

                    <FuelTypography size="caption" weight="medium" color="neutral-default">
                      <FuelSkeleton width="70px" height="40px" shape="text" size="xsmall" />
                    </FuelTypography>
                  </FuelTrowItem>

                  <FuelTrowItem slot="item" className="trow-item-date-paidAt">
                    <FuelTypography size="body-sm" weight="medium" color="neutral-default">
                      <FuelSkeleton width="80px" height="40px" shape="text" size="small" />
                    </FuelTypography>
                  </FuelTrowItem>
                </FuelTrow>
              ))}
            </Styled.ContentTrowScroll>
          </FuelTable>
        </Styled.WrapperTruckerRefundable>
      )}

      <EmptyRefundableList />
    </>
  );
};

export default TruckerRefundableList;
